import {
  get as apiGet, post as apiPost, put as apiPut, TYPE_BROOKLYN_JSON,
} from 'api-client'
import { Map } from 'immutable'
import _assign from 'lodash/assign'
import _cloneDeep from 'lodash/cloneDeep'
import _get from 'lodash/get'
import _set from 'lodash/set'
import _has from 'lodash/has'
import _map from 'lodash/map'
import _omitBy from 'lodash/omitBy'
import _size from 'lodash/size'
import _isUndefined from 'lodash/isUndefined'
import _toString from 'lodash/toString'
import _startsWith from 'lodash/startsWith'
import _parseInt from 'lodash/parseInt'
import _includes from 'lodash/includes'
import _replace from 'lodash/replace'
import { stringify as stringifyQuery } from 'services/query-string'
import { format } from 'services/date-time'
import { EN } from 'services/languages/constants'
import { hasAmbassadorUTM, VALID_REFERRAL_TYPES } from 'services/inbound-tracking'
// eslint-disable-next-line import/no-cycle
import { EMAIL_SIGNUP_GENERIC, SITE_SEGMENT_SHORT_BRAND } from 'services/email-signup'
import {
  ACCOUNT_TYPE_ZUORA_GIFT,
  ACCOUNT_TYPE_FMTV,
  USER_ACCOUNT_PAUSED,
  USER_ACCOUNT_ACTIVE,
  isGaiaDirectPayment,
  USER_ACCOUNT_PENDING,
} from 'services/user-account'
import { get as getConfig } from 'config'

const config = getConfig()
const changePlanAvalibilitySkus = _get(config, 'changePlanAvailabilitySkus', [])
const planGraphQLActive = !!_get(config, ['features', 'checkout', 'planGraphQL'])

export const CHECKOUT_TOTAL_STEPS = 3
export const CHECKOUT_STEP_ONE = 1
export const CHECKOUT_STEP_TWO = 2
export const CHECKOUT_STEP_THREE = 3
export const CHECKOUT_EVENT_STEP_ONE = 1
export const CHECKOUT_EVENT_STEP_TWO = 2
export const CHECKOUT_EVENT_STEP_THREE = 3
export const CHECKOUT_EVENT_STEP_FOUR = 4
export const CHECKOUT_EVENT_STEP_FIVE = 5
export const CHECKOUT_EVENT_STEP_SIX = 6
export const CHECKOUT_EVENT_STEP_SEVEN = 7
export const PAYMENT_TYPE_PAYPAL = 'paypal'
export const PAYMENT_TYPE_CREDIT = 'credit'
export const CHECKOUT_ORDER_ERROR_TYPE_ALREADY_SUBSCRIBED = 'ALREADY_SUBSCRIBED'
export const CHECKOUT_ORDER_ERROR_TYPE_GENERIC = 'GENERIC_ERROR'
export const CHECKOUT_ORDER_ERROR_TYPE_PAYMENT = 'PAYMENT_ERROR'
export const CHECKOUT_ORDER_ERROR_TYPE_UNKNOWN = 'UNKNOWN'
export const CHECKOUT_ORDER_ERROR_TYPE_POSTAL_CODE = 'POSTAL_CODE_ERROR'
export const CHECKOUT_ASYNC_MAX_ATTEMPTS = 90
export const CHECKOUT_ASYNC_DELAY_MILLISECONDS = 1000

const SCHEMA_ORDER_ERROR = {
  error: null,
  success: false,
}

const SCHEMA_EMARSYS_FIELDS = {
  form_name: EMAIL_SIGNUP_GENERIC,
  prospect_behavior_segment: SITE_SEGMENT_SHORT_BRAND,
  prospect_email_comm_br: true,
}

function handleOrderError (res, _dataError) {
  const data = _get(res, 'body', {})
  return createOrderErrorModel(data, _dataError)
}

function createOrderErrorModel (data, _dataError) {
  return _assign(_cloneDeep(SCHEMA_ORDER_ERROR), {
    _dataError,
    success: false,
  })
}

export function userCheckoutAllowed (props, entitled) {
  const {
    auth,
    user = Map(),
    userAccount,
    paytrack,
  } = props
  const accountStatus = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'status'])
  const accountActive = accountStatus === USER_ACCOUNT_ACTIVE
    || accountStatus === USER_ACCOUNT_PAUSED
    || accountStatus === USER_ACCOUNT_PENDING
  const endDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'endDate'])
  const freeTrialNoBillingId = user.getIn(['data', 'freeTrialNoBillingInfo', 'ratePlanId'])
  const gaiaBillingAccount = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'billingAccountId'])
  const accountOwnerUid = _parseInt(user.getIn(['data', 'account_owner_uid']))
  const uid = _parseInt(user.getIn(['data', 'uid']))
  const creationSource = user.getIn(['data', 'creation_source'])
  const creationSourceFMTV = _startsWith(creationSource, ACCOUNT_TYPE_FMTV, 0)
  const creationZuoraGift = _startsWith(creationSource, ACCOUNT_TYPE_ZUORA_GIFT, 0)
  const externalEmail = user.getIn(['data', 'email_registered_externally'])
  const freeTrialNoGaiaBillingAccount = freeTrialNoBillingId && !gaiaBillingAccount
  const fmtvMigratedNoGaiaBillingAccount = (creationSourceFMTV && !gaiaBillingAccount)
    || (!creationSourceFMTV && externalEmail && !gaiaBillingAccount)
  const zuoraGiftBillingAccount = (creationZuoraGift && !gaiaBillingAccount)
  const notAccountOwner = uid && accountOwnerUid && accountOwnerUid !== uid
  const entitledCancelled = entitled && endDate && endDate > format(new Date(), EN, 'YYYY-MM-DD')
  const thirdPartyEntitled = !isGaiaDirectPayment(userAccount, paytrack) && entitled
  const userStatus = user.getIn(['data', 'status'])
  const userStatusBlocked = userStatus === 'blocked'

  if (auth.get('jwt') && !user.get('processing')) {
    if (
      freeTrialNoGaiaBillingAccount
      || fmtvMigratedNoGaiaBillingAccount
      || zuoraGiftBillingAccount
    ) {
      return true
    }

    if (
      accountActive
      || notAccountOwner
      || entitledCancelled
      || thirdPartyEntitled
      || userStatusBlocked
    ) {
      return false
    }
  }

  return true
}

export function cleanSku (sku) {
  const whitespaceRegex = /\s/g

  return _replace(sku, whitespaceRegex, '').toLowerCase()
}

export function userCheckoutAccountChangePlanAllowed (options) {
  const { userAccount = Map(), selectedPlan = Map() } = options
  const changePlanAvalibilitySkusCleaned = _map(changePlanAvalibilitySkus, (s) => cleanSku(s))
  const currentPlanSku = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'planSku'])
  const cleanedSku = cleanSku(selectedPlan.get('sku'))
  const billingAccountId = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'billingAccountId'])

  // if the user is directly billed by Gaia (has a billing account ID), and they have a current plan
  // if the sku is in the allowed change plan sku list and the sku isn't their current plan sku
  if (
    !planGraphQLActive
    && billingAccountId
    && currentPlanSku
    && _includes(changePlanAvalibilitySkusCleaned, cleanedSku)
    && cleanedSku !== cleanSku(currentPlanSku)
    && selectedPlan.get('checkoutEnabled') === true
  ) {
    return true
  }

  // if plan graphql is active, we use the changePlanAllowed field
  if (
    planGraphQLActive
    && billingAccountId
    && currentPlanSku
    && cleanedSku !== cleanSku(currentPlanSku)
    && selectedPlan.get('changePlanAllowed') === true
  ) {
    return true
  }

  return false
}

function formatCaptureEmailData (options) {
  const {
    email,
    sku,
    utm,
    language,
    country,
    firstName,
    inboundTracking,
  } = options
  const data = {
    email,
    sku,
    language,
    country,
    fields: { ...SCHEMA_EMARSYS_FIELDS, first_name: firstName },
    tracking: formatTracking(inboundTracking),
  }
  if (utm) {
    data.utm = utm
  }
  return data
}

export async function captureEmail (options) {
  try {
    const res = await apiPost('v1/checkout/cart-begin', formatCaptureEmailData(options), null, TYPE_BROOKLYN_JSON)
    const data = _get(res, 'body', {})
    return {
      success: _get(data, 'success'),
      errorCode: _get(data, 'errorCode'),
    }
  } catch (e) {
    return { success: false, errorCode: null }
  }
}

/**
 * Format start date override
 */
export function formatStartDateOverride (startDateOverrideInfo) {
  const query = {
    startDateOverride: _get(startDateOverrideInfo, 'formattedEndDate'),
  }
  if (startDateOverrideInfo) {
    return `?${stringifyQuery(query)}`
  }
  return ''
}

export function formatCheckoutV2OrderQuery (options) {
  const { startDateOverrideInfo, velocityCheckToken } = options
  const startDateOverride = _get(startDateOverrideInfo, 'formattedEndDate')
  const query = {}

  if (startDateOverride) {
    _set(query, 'startDateOverride', startDateOverride)
  }

  if (velocityCheckToken) {
    _set(query, 'token', velocityCheckToken)
  }

  if (_size(query) > 0) {
    return `?${stringifyQuery(query)}`
  }

  return ''
}

/**
 * Format a new order
 */
export function formatOrder (options) {
  const {
    token, userInfo, paymentInfo, language, tracking, testarossaUuid,
  } = options
  const shape = {
    token,
    tracking: formatTracking(tracking),
    billing: formatBilling(paymentInfo),
    fields: SCHEMA_EMARSYS_FIELDS,
    user: {
      emailOptIn: _get(userInfo, 'emailOptIn') === true,
      firstName: _get(userInfo, 'firstName'),
      lastName: _get(userInfo, 'lastName'),
      password: _get(userInfo, 'password'),
      email: _get(userInfo, 'email'),
      sendWelcomeEmail: true,
      language,
    },
  }
  if (testarossaUuid) {
    shape.testarossaUuid = testarossaUuid
  }
  return shape
}

/**
 * Format both new orders and reactivation orders
 */
export function formatCheckoutOrder (options) {
  const {
    paymentInfo,
    tracking,
    reactivation,
    emailOptIn,
    fbEventId,
  } = options
  return {
    tracking: formatTracking(tracking, fbEventId),
    billing: formatBilling(paymentInfo),
    emailOptIn,
    ...(!reactivation) && { fields: SCHEMA_EMARSYS_FIELDS },
  }
}

/**
 * Format billing information sent in an order
 */
export function formatBilling (paymentInfo) {
  const {
    currencyIso,
    country,
    postalCode,
    productRatePlanId,
    planSku,
    adyen,
  } = paymentInfo
  const billingData = {
    country,
    postalCode,
    currency: currencyIso,
    productRatePlanId,
    planSku,
    adyen,
  }

  return billingData
}

/**
 * Format tracking that is sent in an order
 */
export function formatTracking (inboundTracking, fbEventId) {
  const trackingData = {}

  if (Map.isMap(inboundTracking) && inboundTracking.size > 0) {
    const data = inboundTracking.get('data', Map())
    const chan = data.get('chan')
    const cid = data.get('cid')
    const linkshare = data.get('linkshare', Map())
    const referralId = data.get('rfd')
    const source = data.get('source')
    const sourceId = data.get('sourceId')
    const referralData = _omitBy({ referralId, source, sourceId }, (value, key) => {
      if (key === 'source') {
        return !VALID_REFERRAL_TYPES.includes(value)
      }
      return _isUndefined(value)
    })
    const utm = data.getIn(['strings', 'utm']) || _get(data.get('strings'), 'utm')
    const utmJS = utm && _has(utm, 'toJS') ? utm.toJS() : utm
    const transactionId = data.get('transactionId')
    const offerId = data.get('offerId')
    const affiliateId = data.get('affiliateId')
    const everflow = {}

    if (linkshare.size > 0) {
      _set(
        trackingData,
        'linkshare',
        _has(linkshare, 'toJS') || Map.isMap(linkshare)
          ? linkshare.toJS()
          : linkshare,
      )
    }

    if (chan) {
      _set(trackingData, 'chan', _toString(chan))
    }

    if (cid) {
      _set(trackingData, 'cid', _toString(cid))
    }

    if (utmJS) {
      _set(trackingData, 'utm', utmJS)
    }

    if (_size(referralData) > 0 && !hasAmbassadorUTM(data)) {
      _set(trackingData, 'referral', referralData)
    }

    // Everflow tracking
    if (transactionId) {
      _set(everflow, 'transactionId', transactionId)
    }

    if (offerId) {
      _set(everflow, 'offerId', `${offerId}`)
    }

    if (affiliateId) {
      _set(everflow, 'affiliateId', affiliateId)
    }

    if (_size(everflow) > 0) {
      _set(trackingData, 'everflow', everflow)
    }
  }

  // Set the facebook event id
  if (fbEventId) {
    _set(trackingData, 'fbEventId', fbEventId)
  }

  return trackingData
}

function handleCheckoutOrderResponse (res, _dataError) {
  const data = _get(res, 'body', {})
  return {
    _dataError,
    success: _get(data, 'success'),
    errorCode: _get(data, 'errorCode'),
    billing: _get(data, 'billing'),
  }
}

/**
 * Create checkout orders for both new users and reactivation users
 */
export async function createCheckoutOrder (options) {
  const {
    paymentInfo,
    tracking,
    auth,
    startDateOverrideInfo,
    reactivation,
    emailOptIn,
    fbEventId,
  } = options
  const order = formatCheckoutOrder({
    paymentInfo,
    tracking,
    reactivation,
    emailOptIn,
    fbEventId,
  })
  const startDateOverride = formatStartDateOverride(startDateOverrideInfo)
  try {
    const res = await apiPut(`v1/billing/account${startDateOverride}`, order, { auth }, TYPE_BROOKLYN_JSON)
    return handleCheckoutOrderResponse(res)
  } catch (e) {
    return handleOrderError({}, true)
  }
}

function handleCheckoutV2OrderResponse (res, _dataError) {
  const data = _get(res, 'body', {})
  const uuid = _get(data, 'uuid')

  return {
    _dataError: _dataError || !uuid,
    success: _get(data, 'success', false),
    checkoutId: _get(data, 'uuid', null),
  }
}

/**
 * Create checkout v2 orders for both new users and reactivation users
 */
export async function createCheckoutV2Order (options) {
  const {
    paymentInfo,
    tracking,
    auth,
    startDateOverrideInfo,
    reactivation,
    emailOptIn,
    fbEventId,
    velocityCheckToken,
  } = options
  const order = formatCheckoutOrder({
    paymentInfo,
    tracking,
    reactivation,
    emailOptIn,
    fbEventId,
  })
  const queryData = formatCheckoutV2OrderQuery({ startDateOverrideInfo, velocityCheckToken })

  try {
    const res = await apiPost(`v2/billing/checkout${queryData}`, order, { auth }, TYPE_BROOKLYN_JSON)
    return handleCheckoutV2OrderResponse(res)
  } catch (e) {
    return handleOrderError({}, true)
  }
}

function handleGetCheckoutOrderData (res, _dataError) {
  const data = _get(res, 'body', {})
  const errorCode = _get(data, ['billingResponse', 'errorCode']) || _get(data, ['errors', 0])

  if (errorCode) {
    _set(data, 'errorCode', errorCode)
  }

  if (_dataError) {
    _set(data, '_dataError', _dataError)
  }

  return data
}

/**
 * Get checkout order status / data
 */
export async function getCheckoutOrderData (options) {
  const {
    checkoutId,
    auth,
  } = options

  try {
    const res = await apiGet(`v2/billing/checkout/${checkoutId}`, null, { auth }, TYPE_BROOKLYN_JSON)
    return handleGetCheckoutOrderData(res)
  } catch (e) {
    return handleGetCheckoutOrderData({}, true)
  }
}
